import React from 'react';
import { Link } from 'react-router-dom';
import { useSectionRoute } from '../../hooks/useSectionRoute';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Meter } from '../../components/ui/Meter';
import { Tabs } from '../../components/ui/Tabs';
import { academicActivity, accommodationPressure, careerOutcomes, engagementEvents, feedbackByCategory, institutionSummary, populationByFaculty, recurringIssues, safetySnapshot } from '../../data/institution';
import { institutionServiceLoad } from '../../data/services';
import { naira, percent } from '../../utils/format';
import { AttentionBoard } from '../../components/operator/AttentionBoard';
import { DataTable } from '../../components/ui/DataTable';
import { BadgeCheckIcon } from 'lucide-react';
const TABS = ['Today', 'Overview', 'Students', 'Services', 'Feedback',
// Named for what it is — aggregate trends — so it cannot be mistaken for
// the operational safety desk at /institution/safety, which receives live
// alerts. The two are different jobs and now have different addresses.
'Safety trends', 'Accommodation', 'Career'] as const;
export function InstitutionOverview() {
  const [tab, setTab] = useSectionRoute('/institution', TABS);

  // Derived rather than asserted: the worst feedback category, the tightest
  // housing area and the issues students keep raising.
  const worstFeedback = [...feedbackByCategory].sort((a, b) => b.open - a.open)[0];
  const tightest = [...accommodationPressure].sort((a, b) => a.listedBeds - b.listedBeds)[0];
  const rising = recurringIssues.filter(issue => issue.trend.includes('+') || issue.trend.toLowerCase().includes('up'));
  const leastEngaged = [...populationByFaculty].sort((a, b) => a.engaged / a.students - b.engaged / b.students)[0];
  return <div className="space-y-6" data-id="element-9126" data-mp="23y6ppklzjm0l">
      <PageHeader eyebrow={institutionSummary.name} title="{institution} console" description={`${institutionSummary.campus} · ${institutionSummary.integrationLevel}. You only see data your institution is authorized to access, and never another institution's.`} data-id="element-9127" data-mp="241kqwpiwxp6g" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="{institution} sections" data-id="element-9128" data-mp="1o7fpukhy4qe2" />

      {tab === 'Today' && <AttentionBoard emptyNote="Nothing in the aggregate figures is moving in a direction that needs a response this week." items={[...(worstFeedback ? [{
      id: 'feedback',
      title: `${worstFeedback.category} feedback is not being closed`,
      detail: `${worstFeedback.open} items are open and the median time to resolution is ${worstFeedback.medianDays} days. Students see the delay, not the reason for it.`,
      urgency: 'now' as const,
      action: {
        label: 'Open feedback',
        to: '/institution/feedback'
      }
    }] : []), ...rising.map(issue => ({
      id: issue.issue,
      title: issue.issue,
      detail: `Raised ${issue.reports} times and trending ${issue.trend}. A recurring report is a signal about a process, not about the students raising it.`,
      urgency: 'soon' as const
    })), ...(tightest ? [{
      id: 'housing',
      title: `Accommodation is tightest in ${tightest.neighbourhood}`,
      detail: `${tightest.listedBeds} beds listed against ${tightest.demand} demand, median ${tightest.medianRent}. Campuzon lists what landlords publish; it does not create supply.`,
      urgency: 'soon' as const
    }] : []), ...(leastEngaged ? [{
      id: 'engagement',
      title: `${leastEngaged.faculty} engages least`,
      detail: `${leastEngaged.engaged.toLocaleString()} of ${leastEngaged.students.toLocaleString()} students are active. Low engagement may mean the faculty publishes little here, not that its students are disengaged.`,
      urgency: 'watch' as const
    }] : [])]} summary={[{
      label: 'Active this week',
      value: institutionSummary.activeThisWeek.toLocaleString(),
      note: `of ${institutionSummary.students.toLocaleString()} students.`
    }, {
      label: 'Integration level',
      value: institutionSummary.integrationLevel,
      note: 'Decides what your students can prove about their enrolment.'
    }, {
      label: 'Verification queue',
      value: 'Open',
      note: 'Affiliation requests waiting on your registry.'
    }]} data-id="element-9129" data-mp="vkun6nw0srv7" />}

      {tab === 'Services' && <div className="grid gap-6 lg:grid-cols-3" data-id="element-9130" data-mp="3bbe9sozli5g2">
          <Panel className="lg:col-span-2" title="Academic service queues" description="Requests students have raised through Campuzon for services this institution provides." bodyClassName="p-0" data-id="element-9131" data-mp="3ga7nahevtwg8">
            <ul className="divide-y divide-line" data-id="element-9132" data-mp="3pzhhq3x77r7i">
              {institutionServiceLoad.queues.map(queue => <li key={queue.label} className="px-5 py-3.5" data-id="element-9133" data-mp="3v0ydjl0tsnh8">
                  <p className="flex flex-wrap items-baseline gap-2" data-id="element-9134" data-mp="2wcohjuy8por">
                    <span className="text-[13px] font-medium text-ink" data-id="element-9135" data-mp="7unz3wt2j481">{queue.label}</span>
                    {queue.overdue > 0 && <Badge tone="warn" data-id="element-9136" data-mp="24nhfqesyzo8k">{queue.overdue} past target</Badge>}
                    <span className="cz-num ml-auto text-sm font-semibold text-ink" data-id="element-9137" data-mp="29l944yueos80">{queue.open} open</span>
                  </p>
                  <p className="cz-num mt-0.5 text-[12px] text-ink-subtle" data-id="element-9138" data-mp="3cxg7n6ke582h">
                    Median {queue.medianDays} days to completion
                  </p>
                </li>)}
            </ul>
          </Panel>

          <div className="space-y-6" data-id="element-9139" data-mp="3lv0b8m5q7gg9">
            <Panel title="What you offer through Campuzon" data-id="element-9140" data-mp="3gxic1nhigflg">
              <ul className="space-y-1.5" data-id="element-9141" data-mp="3h04i1ior6n1k">
                {institutionServiceLoad.officiallyProvided.map(item => <li key={item} className="flex items-start gap-2 text-[13px] leading-6 text-ink" data-id="element-9142" data-mp="3qw7huhw4u9lc">
                    <BadgeCheckIcon className="mt-1 h-3.5 w-3.5 shrink-0 text-ok-700" data-id="element-9143" data-mp="8i6y0n0fqekr" /> {item}
                  </li>)}
              </ul>
              <p className="mt-3 text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-9144" data-mp="4h4ys66iq2is">
                Not offered here
              </p>
              <ul className="mt-1 space-y-1" data-id="element-9145" data-mp="dhjkv7rb3lnc">
                {institutionServiceLoad.notOfferedThroughCampuzon.map(item => <li key={item} className="text-[13px] leading-6 text-ink-muted" data-id="element-9146" data-mp="x44qbedtg0bx">
                    · {item}
                  </li>)}
              </ul>
              <p className="mt-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-9147" data-mp="n55mqa7wquvs">
                You control which services appear as officially provided. Anything you do not list is
                shown to students as an independent or self-managed route.
              </p>
            </Panel>

            <Panel title="Verified providers operating here" data-id="element-9148" data-mp="j3brihghej30">
              <ul className="divide-y divide-line" data-id="element-9149" data-mp="j4muwg60lotl">
                {institutionServiceLoad.providers.map(provider => <li key={provider.name} className="flex flex-wrap items-center gap-2 py-2 first:pt-0" data-id="element-9150" data-mp="4ckzp9ew3jzw">
                    <span className="min-w-0 flex-1 text-[13px] text-ink" data-id="element-9151" data-mp="3rj3sktyf4gme">{provider.name}</span>
                    <Badge tone="ok" data-id="element-9152" data-mp="3vkpef4sxk58q">{provider.status}</Badge>
                    <span className="cz-num text-[12px] text-ink-subtle" data-id="element-9153" data-mp="3vk7b5o7uoqrb">{provider.requests}</span>
                  </li>)}
              </ul>
              <p className="mt-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-9154" data-mp="av7mycw69lfi">
                These are independent providers verified by Campuzon. None of them acts for this
                institution, and none can be presented as doing so.
              </p>
            </Panel>
          </div>
        </div>}

      {tab === 'Overview' && <div className="grid gap-6 lg:grid-cols-3" data-id="element-9155" data-mp="3t2x52crst55j">
          <Panel className="lg:col-span-2" title="Academic activity this semester" bodyClassName="p-0" data-id="element-9156" data-mp="3oa3xzi8uxkqa">
            <ul className="divide-y divide-line" data-id="element-9157" data-mp="3jb0zyhraoert">
              {academicActivity.map(item => <li key={item.label} className="flex items-baseline justify-between gap-4 px-5 py-3.5" data-id="element-9158" data-mp="3ed8zv0ghxmcj">
                  <span className="text-[13px] text-ink-muted" data-id="element-9159" data-mp="39dmnet8hsa4s">{item.label}</span>
                  <span className="flex items-baseline gap-3" data-id="element-9160" data-mp="35c1ax8zvdxv4">
                    <span className="cz-num text-sm font-semibold text-ink" data-id="element-9161" data-mp="35dvzcxaco83y">{item.value}</span>
                    <span className="cz-num w-28 text-right text-[12px] text-ink-subtle" data-id="element-9162" data-mp="1kvtjq5z38b0s">
                      {item.delta}
                    </span>
                  </span>
                </li>)}
            </ul>
          </Panel>
          <Panel title="Population" data-id="element-9163" data-mp="1yu1nawqmqakx">
            <p className="cz-num text-3xl font-semibold tracking-tight text-ink" data-id="element-9164" data-mp="1yt00xw45y1nw">
              {institutionSummary.students.toLocaleString()}
            </p>
            <p className="mt-1 text-[13px] text-ink-muted" data-id="element-9165" data-mp="164b8sml356oa">Students with a Campuzon account</p>
            <div className="mt-4" data-id="element-9166" data-mp="1b3fg17og8ubz">
              <Meter value={percent(institutionSummary.activeThisWeek, institutionSummary.students)} label="Active this week" trailing={`${percent(institutionSummary.activeThisWeek, institutionSummary.students)}%`} data-id="element-9167" data-mp="1b35r7nf54t7v" />
            </div>
          </Panel>
          <Panel className="lg:col-span-3" title="Engagement — events" bodyClassName="p-0" data-id="element-9168" data-mp="ncif0py6zhfi">
            <ul className="divide-y divide-line" data-id="element-9169" data-mp="15r01n3192ttc">
              {engagementEvents.map(event => <li key={event.title} className="flex flex-wrap items-center gap-4 px-5 py-3.5" data-id="element-9170" data-mp="1ap1p005plfam">
                  <div className="min-w-0 flex-1" data-id="element-9171" data-mp="1fq8xk1foeiic">
                    <p className="text-[13px] font-medium text-ink" data-id="element-9172" data-mp="1kpc0ngc3ieno">{event.title}</p>
                    <p className="text-[12px] text-ink-muted" data-id="element-9173" data-mp="n11zt4o1pwy5">{event.organizer}</p>
                  </div>
                  <div className="w-48" data-id="element-9174" data-mp="rzvakpby79um">
                    <Meter value={percent(event.registrations, event.capacity)} trailing={`${event.registrations}/${event.capacity}`} data-id="element-9175" data-mp="s0wp53s6aeh3" />
                  </div>
                </li>)}
            </ul>
          </Panel>
        </div>}

      {tab === 'Students' && <Panel title="{student:plural} by faculty" bodyClassName="p-0" data-id="element-9176" data-mp="99nc4kj717kr">
          <ul className="divide-y divide-line" data-id="element-9177" data-mp="97so2xcmkzcg">
            {populationByFaculty.map(row => <li key={row.faculty} className="flex flex-wrap items-center gap-4 px-5 py-3.5" data-id="element-9178" data-mp="2mj3ux9m79xbm">
                <p className="w-40 shrink-0 text-[13px] font-medium text-ink" data-id="element-9179" data-mp="2ij36cw5e05qu">{row.faculty}</p>
                <div className="min-w-[180px] flex-1" data-id="element-9180" data-mp="2dhdvbgi5zs2r">
                  <Meter value={percent(row.engaged, row.students)} label="Engaged on Campuzon" trailing={`${percent(row.engaged, row.students)}%`} data-id="element-9181" data-mp="28knj549hdgmm" />
                </div>
                <p className="cz-num w-32 text-right text-[13px] text-ink-muted" data-id="element-9182" data-mp="1yt15l19o75gp">
                  {row.students.toLocaleString()} students
                </p>
              </li>)}
          </ul>
        </Panel>}

      {tab === 'Feedback' && <div className="grid gap-6 lg:grid-cols-2" data-id="element-9183" data-mp="1kti06g88u883">
          <Panel title="Feedback by category" bodyClassName="p-0" data-id="element-9184" data-mp="1kwdyi5o06yx2">
            <ul className="divide-y divide-line" data-id="element-9185" data-mp="1fw00lgg495cr">
              {feedbackByCategory.map(row => <li key={row.category} className="flex items-center gap-4 px-5 py-3.5" data-id="element-9186" data-mp="1669nfazaduu0">
                  <p className="min-w-0 flex-1 text-[13px] font-medium text-ink" data-id="element-9187" data-mp="1b7qn5361bb90">{row.category}</p>
                  <span className="cz-num text-[13px] text-warn-700" data-id="element-9188" data-mp="x8hd6l8f9ddh">{row.open} open</span>
                  <span className="cz-num text-[13px] text-ink-muted" data-id="element-9189" data-mp="119j5ei3x8p5y">{row.resolved} resolved</span>
                  <span className="cz-num w-20 text-right text-[13px] text-ink-muted" data-id="element-9190" data-mp="1ozn88n9ackvw">
                    {row.medianDays}d median
                  </span>
                </li>)}
            </ul>
          </Panel>
          <Panel title="Recurring issues" bodyClassName="p-0" data-id="element-9191" data-mp="1g5s5flvmz6jf">
            <ul className="divide-y divide-line" data-id="element-9192" data-mp="1k6kcq9ky71d2">
              {recurringIssues.map(issue => <li key={issue.issue} className="flex items-center gap-3 px-5 py-3.5" data-id="element-9193" data-mp="27w57rqudlp2p">
                  <p className="min-w-0 flex-1 text-[13px] text-ink" data-id="element-9194" data-mp="2co6fczglmrme">{issue.issue}</p>
                  <span className="cz-num text-[13px] font-medium text-ink" data-id="element-9195" data-mp="18fpgwlmd58i">{issue.reports}</span>
                  <Badge tone={issue.trend === 'Rising' ? 'warn' : issue.trend === 'Falling' ? 'ok' : 'neutral'} data-id="element-9196" data-mp="3sfs8lh62bsr2">
                    {issue.trend}
                  </Badge>
                </li>)}
            </ul>
          </Panel>
        </div>}

      {tab === 'Safety trends' && <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-9197" data-mp="3eexwanfw97n4">
          {safetySnapshot.map(item => <Panel key={item.label} data-id="element-9198" data-mp="39h6926q729p9">
              <p className="cz-num text-2xl font-semibold tracking-tight text-ink" data-id="element-9199" data-mp="39i7gm1804e28">{item.value}</p>
              <p className="mt-1 text-[13px] leading-5 text-ink-muted" data-id="element-9200" data-mp="3oeymmtc6ko0t">{item.label}</p>
            </Panel>)}
          <div className="sm:col-span-2 lg:col-span-4" data-id="element-9201" data-mp="3jgx0u0ndealn">
            <Panel title="Handling standards" data-id="element-9202" data-mp="13m3gcufdlo98">
              <p className="text-body-sm leading-6 text-ink-muted" data-id="element-9203" data-mp="yobqs727ee87">
                These are aggregate trends, not live cases. Anonymous reports never carry identifying
                data into this console. Confidential reports are opened only by named safeguarding
                staff, and every access is written to the audit log.
              </p>
              <p className="mt-3 text-body-sm text-ink-muted" data-id="element-9204" data-mp="3dcilel0lsx1v">
                Alerts and individual reports are answered on the{' '}
                <Link to="/institution/safety" className="font-medium text-brand-700 underline" data-id="element-9205" data-mp="3ia0p9wzrkm30">
                  campus safety desk
                </Link>
                .
              </p>
            </Panel>
          </div>
        </div>}

      {tab === 'Accommodation' && <Panel title="Accommodation pressure by neighbourhood" description="Aggregated and anonymized. No individual student's housing activity is exposed here." bodyClassName="p-0" data-id="element-9206" data-mp="xzxqvbtv09m">
          <DataTable caption="Accommodation pressure by neighbourhood" rowKey={row => row.neighbourhood} rows={accommodationPressure} columns={[{
        header: 'Neighbourhood',
        primary: true,
        cell: row => row.neighbourhood
      }, {
        header: 'Students seeking',
        align: 'right',
        cell: row => <span className="cz-num" data-id="element-9208" data-mp="aztuqswyuffw">{row.demand.toLocaleString()}</span>
      }, {
        header: 'Beds listed',
        align: 'right',
        cell: row => <span className="cz-num" data-id="element-9209" data-mp="12qzx7g05sp8c">{row.listedBeds.toLocaleString()}</span>
      }, {
        header: 'Median rent',
        align: 'right',
        cell: row => <span className="cz-num" data-id="element-9210" data-mp="tyo75v8yj3sw">{naira(row.medianRent)}</span>
      }, {
        header: 'Pressure',
        status: true,
        cell: row => {
          const ratio = Math.round(row.demand / row.listedBeds * 10) / 10;
          return <Badge tone={ratio >= 3 ? 'danger' : ratio >= 2 ? 'warn' : 'ok'} data-id="element-9211" data-mp="3fx6bw9zhm158">
                      {ratio} students per bed
                    </Badge>;
        }
      }]} data-id="element-9207" data-mp="evo83989qp0f" />
        </Panel>}

      {tab === 'Career' && <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-9212" data-mp="3b6pyuqzkwhje">
          {careerOutcomes.map(item => <Panel key={item.label} data-id="element-9213" data-mp="2x6owf73o121t">
              <p className="cz-num text-2xl font-semibold tracking-tight text-ink" data-id="element-9214" data-mp="2x5nau8p0wx5u">{item.value}</p>
              <p className="mt-1 text-[13px] leading-5 text-ink-muted" data-id="element-9215" data-mp="327w4765t618h">{item.label}</p>
            </Panel>)}
        </div>}
    </div>;
}